<script setup lang="ts">
import { ZhPanel, ZhSectionHeading } from '@zerohuman/ui'
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import AgentConnectionsPanel from '@/components/AgentConnectionsPanel.vue'
import { teammateHandoff } from '@/lib/teammateHandoff'

/**
 * `/agents?reconnect=<connectionId>` (#1719): a lost Claude subscription's Blockers row opens the page on that
 * connection's editor, in its existing replace-in-place mode, so Reconnect is the Agents page's own flow.
 */
const route = useRoute()
const reconnect = computed(() => {
    const value = route.query['reconnect']
    return typeof value === 'string' && value ? value : undefined
})

/**
 * `/agents?connect=member&memberId=<id>` (#1758): Add a teammate's "Give this teammate its own account" lands here once
 * the teammate exists, with the page held to that member, so its Add opens the secure connect card for their layer.
 */
const memberLayer = computed(() => {
    const value = route.query['memberId']
    return route.query['connect'] === 'member' && typeof value === 'string' && value ? value : undefined
})

/**
 * What comes after the account, when Add a teammate kept it for this tab: a described job's first task, finished on
 * Create task with its sentence, role and team, and the teammate's page, which lists the tools still to connect.
 */
const handoff = computed(() => (memberLayer.value ? teammateHandoff(memberLayer.value) : null))
</script>

<template>
    <section class="w-full min-w-0 space-y-5 p-4 md:p-8">
        <ZhPanel v-if="memberLayer && handoff" class="space-y-2" data-testid="teammate-next">
            <ZhSectionHeading>Next for your new teammate</ZhSectionHeading>
            <p class="text-muted-foreground text-sm">Connect their own account below, then carry on.</p>
            <ul class="space-y-1 text-sm">
                <li v-if="handoff.firstTask">
                    <RouterLink
                        class="underline"
                        data-testid="teammate-first-task"
                        :to="{ path: '/tasks/new', query: { ...handoff.firstTask } }"
                    >
                        Write their first task: {{ handoff.firstTask.goal }}
                    </RouterLink>
                </li>
                <li>
                    <RouterLink class="underline" data-testid="teammate-page" :to="`/members/${memberLayer}`">
                        Open their page{{ handoff.tools.length ? ' and connect the tools they need' : '' }}
                    </RouterLink>
                </li>
            </ul>
        </ZhPanel>
        <AgentConnectionsPanel
            page
            :layer="memberLayer ? 'member' : undefined"
            :layer-id="memberLayer"
            :reconnect="reconnect"
        />
    </section>
</template>
